import {
  keepPreviousData,
  skipToken,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { fetchUserFillsPaginated } from '#/api/apiExchange';
import { transformOrderFillToApiFill } from '#/api/apiUtils';
import { ApiFillsPaginatedRaw } from '#/api/types';
import { useWebsocketEventListener } from '#/components/hooks';
import { updateMinedFillInArray } from '#/components/trading/MyTrades/fillsFetcherHook';
import { getPaginationPerPageLimit } from '#/config/configUtils';
import { PAGINATION_PAGES_LIMIT } from '#/config/constants';
import { throttleInstance } from '#/lib/throttle';
import { useSelector } from '#/reduxState/store';

import { minutes, seconds } from './utils';

const throttleInvalidation = throttleInstance(2000);

export const PAGE_LIMIT = getPaginationPerPageLimit(50);

export const useGlobalFillsPaginatedInvalidator = () => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const queryClient = useQueryClient();

  useWebsocketEventListener('orders', updatedOrder => {
    const orderFills = updatedOrder.data.fills;

    if (!orderFills || orderFills?.length === 0) {
      return;
    }
    throttleInvalidation(() => {
      queryClient.invalidateQueries({
        queryKey: ['fillsPaginated', walletAddress],
      });
    });
  });
};

const useLiveUpdatePaginatedFills = (searchMarket: string | undefined) => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);

  const queryClient = useQueryClient();

  useWebsocketEventListener('orders', updatedOrder => {
    const orderFills = updatedOrder.data.fills;

    if (!orderFills || orderFills?.length === 0) {
      return;
    }

    // Note: Queries will be invalidated by global subscription `useGlobalFillsPaginatedInvalidator`
    //  We only have to do mutation here to update data

    const websocketFills = orderFills.map(fill =>
      transformOrderFillToApiFill(updatedOrder.data, fill)
    );

    // If one of new fills is liquidation, we have to reload data
    // from internal API to provide `indexPrice` (Miss in WS) P9-5441
    if (websocketFills.some(fill => fill.type === 'liquidation')) {
      throttleInvalidation(() => {
        queryClient.invalidateQueries({
          queryKey: ['fillsPaginated', walletAddress],
        });
      });
      return;
    }

    queryClient.setQueryData<ApiFillsPaginatedRaw>(
      ['fillsPaginated', walletAddress, searchMarket, 1],
      existingData => {
        if (!existingData) {
          return undefined; // README P9-4765
        }
        // Make sure we accept only fills with higher sequence number than last trade
        const lastFill =
          existingData.items && existingData.items.length > 0
            ? existingData.items[0]
            : null;
        const newFillsWithHigherSequence = websocketFills.filter(fill =>
          fill.type === 'liquidation'
            ? fill.time > (lastFill?.time || 0)
            : (fill.sequence || 0) > (lastFill?.sequence || 0)
        );
        if (newFillsWithHigherSequence.length === 0) {
          console.log('Order updated, but no new fills found (with higher SN)');
          return existingData;
        }
        return {
          items: [
            ...newFillsWithHigherSequence,
            ...(existingData.items || []),
          ].slice(0, PAGE_LIMIT),
          count: existingData.count + newFillsWithHigherSequence.length,
        };
      }
    );
  });

  useWebsocketEventListener('webclient', message => {
    const settledEvent = message.data;
    if (settledEvent.event !== 'transaction_settled') {
      return;
    }
    if (settledEvent.action === 'executeTrade') {
      const updateFillsIfNeeded = (
        existingList?: ApiFillsPaginatedRaw
      ): ApiFillsPaginatedRaw | undefined => {
        if (!existingList) {
          return undefined; // README P9-4765
        }
        const updatedFills = updateMinedFillInArray(existingList?.items || [], {
          fillId: settledEvent.payload.fillId,
          txHash: settledEvent.payload.txHash,
        });
        return {
          items: updatedFills,
          count: existingList?.count || 0,
        };
      };

      queryClient.setQueryData<ApiFillsPaginatedRaw>(
        ['fillsPaginated', walletAddress, searchMarket, 1],
        updateFillsIfNeeded
      );
      queryClient.setQueryData<ApiFillsPaginatedRaw>(
        ['fillsPaginated', walletAddress, searchMarket, 1],
        updateFillsIfNeeded
      );
    }
  });
};

/** https://tanstack.com/query/v4/docs/react/guides/paginated-queries */
export const useFillsPaginatedQuery = ({
  walletAddress,
  isVault,
  searchMarket,
  page,
}: {
  walletAddress: string;
  isVault: boolean;
  searchMarket: string | undefined;
  page: number;
}) => {
  useLiveUpdatePaginatedFills(isVault ? undefined : searchMarket);

  return useQuery({
    queryKey: [
      // Different key to prevent WS live data missmatch
      isVault ? 'vaultFillsPaginated' : 'fillsPaginated',
      walletAddress,
      isVault,
      searchMarket,
      page,
    ],
    queryFn:
      !walletAddress || page > PAGINATION_PAGES_LIMIT
        ? skipToken
        : () =>
            fetchUserFillsPaginated({
              wallet: walletAddress,
              isVault,
              page,
              limit: PAGE_LIMIT,
              market: searchMarket,
            }),
    // We should keep time short, because there might be ws events
    // when we are not subscribed to paginated query
    staleTime: seconds(5),
    refetchOnWindowFocus: false,
    refetchInterval: minutes(7),
    // We have to keep previous data for better
    // UX  on pagination (switching pages)
    placeholderData: keepPreviousData,
  });
};
